import React, { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import FlowLayout from '../../components/layout/FlowLayout';
import { FLOWS_ENUM } from '../../common/enums';
import {
  selectConfigExpressLocalContactVerbiage,
  selectConfigExpressRegistrationHoa,
  selectConfigExpressRegistrationLocalContactOptionalFields,
  selectConfigExpressRegistrationPeopleHoaDescription,
  selectConfigExpressRegistrationPeopleLocalContactDescription,
  selectConfigExpressRegistrationPeoplePropertyManagementDescription,
  selectConfigExpressRegistrationPropertyManager,
} from '../../store/slices/config/configSelectors';
import { ApplicationData } from 'src/types/flow';
import {
  setCurrentStep,
  setStepCompletedStatus,
} from 'src/store/slices/flow/flowSlice';
import usePlaceNavigate from 'src/hooks/usePlaceNavigate';
import {
  selectCurrentStep,
  selectNextStepPath,
} from 'src/store/slices/flow/flowSelectors';
import { ContactForm, ContactFormRef } from 'src/components/form/ContactForm';
import { useAppDispatch } from 'src/store/store';
import { PersonOptionalFields } from 'src/types/configuration';

interface ContactsPageProps {
  data: ApplicationData;
  flow: FLOWS_ENUM;
  updateApplicationData: (data: ApplicationData) => void;
}

export const ContactsPage = ({
  data,
  flow,
  updateApplicationData,
}: ContactsPageProps) => {
  const contactFormRef = useRef<ContactFormRef>(null);
  const dispatch = useAppDispatch();
  const placeNavigate = usePlaceNavigate();

  const { registrant } = data;

  const propertyManagerConfiguration = useSelector(
    selectConfigExpressRegistrationPropertyManager
  );

  const hoaConfiguration = useSelector(selectConfigExpressRegistrationHoa);
  const hoaDescription = useSelector(
    selectConfigExpressRegistrationPeopleHoaDescription
  );

  const selectedLocalContactOptionalFields = useSelector(
    selectConfigExpressRegistrationLocalContactOptionalFields
  );
  const propertyManagerDescription = useSelector(
    selectConfigExpressRegistrationPeoplePropertyManagementDescription
  );
  const localContactDescription = useSelector(
    selectConfigExpressRegistrationPeopleLocalContactDescription
  );
  const selectedLocalContactVerbiage = useSelector(
    selectConfigExpressLocalContactVerbiage
  );

  const selectedNextStepPath = useSelector(selectNextStepPath);
  const selectedCurrentStep = useSelector(selectCurrentStep);

  const updateData = () => {
    const data = contactFormRef.current?.getValues() as ApplicationData;
    updateApplicationData(data);
  };

  const handleNextClick = async () => {
    if (!registrant?.registrantType && flow !== FLOWS_ENUM.ADMIN_UPDATE) return;
    if (await contactFormRef.current?.validateForm()) {
      updateData();
      dispatch(
        setStepCompletedStatus({ stepNum: selectedCurrentStep, status: true })
      );
      dispatch(setCurrentStep(selectedCurrentStep + 1));
      placeNavigate('/' + selectedNextStepPath);
    }
  };

  const handleBackClick = () => {
    updateData();
  };

  const localContactData = useMemo(
    () => ({
      description: localContactDescription,
      enabled: true,
      optionalFields: selectedLocalContactOptionalFields as unknown as Record<
        string,
        unknown
      >,
      verbiage: selectedLocalContactVerbiage,
    }),
    [
      localContactDescription,
      selectedLocalContactOptionalFields,
      selectedLocalContactVerbiage,
    ]
  );

  const propertyManagerData = useMemo(
    () => ({
      description: propertyManagerDescription,
      enabled: propertyManagerConfiguration?.enable ?? false,
      optionalFields: propertyManagerConfiguration as PersonOptionalFields,
    }),
    [propertyManagerConfiguration, propertyManagerDescription]
  );

  const hoaData = useMemo(
    () => ({
      description: hoaDescription,
      enabled: hoaConfiguration?.enable ?? false,
      optionalFields: (hoaConfiguration ?? {}) as PersonOptionalFields,
    }),
    [hoaConfiguration, hoaDescription]
  );

  return (
    <FlowLayout
      flow={flow}
      title="Property Contacts"
      onNext={handleNextClick}
      onBack={handleBackClick}
      omitNavigationForward
      showNext
      showBack
    >
      <ContactForm
        data={data}
        flow={flow}
        hoaData={hoaData}
        localContactData={localContactData}
        propertyManagerData={propertyManagerData}
        ref={contactFormRef}
      />
    </FlowLayout>
  );
};
